import { Controller } from 'stimulus';
import gsap from 'gsap';
import jump from 'jump.js';
import toArray from '../helpers/toArray';
import csrfToken from '../helpers/csrfToken';
import formDataToArray from './helpers/formDataToArray';
import controllerForElement from '../helpers/controllerForElement';

class SharedFilter extends Controller {
  connect() {
    this.page = 1;

    if (typeof URLSearchParams === 'function') {
      const urlParameters = new URLSearchParams(window.location.search);

      if (urlParameters.get('page')) {
        this.page = parseInt(urlParameters.get('page'), 10);
      }
    }

    this.filters = toArray(this.element.querySelectorAll('.select'));

    this.boundOnFilterChange = this.onFilterChange.bind(this);

    this.filters.forEach((filter) => {
      filter.addEventListener('change', this.boundOnFilterChange);
    });

    if (this.hasResetFilterTarget) {
      this.resetFilterTarget.style.display = this.hasActiveFilters() ? 'block' : 'none';
    }
  }

  onFilterChange() {
    this.page = 1;
    this.runFilter();
  }

  onSearchChange() {
    this.page = 1;
    this.runFilter();
  }

  onBackToOverview() {
    if (this.hasFilterBarTarget) {
      jump(this.filterBarTarget);
    }

    this.onResetFilters();
  }

  onResetFilters() {
    this.page = 1;

    // Reset search bar
    controllerForElement(this.searchBarTarget, 'search-bar').then((searchBarController) => {
      searchBarController.reset();
    });

    // Reset filters
    this.filters.forEach((filterNode) => {
      const filterInputNode = filterNode.querySelector('[data-target="select.inputField"]');
      const listNode = filterNode.querySelector('[data-target="select.list"]');
      const toggleNode = filterNode.querySelector('[data-target="select.toggle"]');

      if (!filterNode || !listNode || !toggleNode) {
        return;
      }

      // Remove the value on the hidden input, in order for the filter to not pick it up.
      filterInputNode.value = '';

      // Change the selected option to be not-selected
      const selectedOption = listNode.querySelector('li[aria-selected="true"]');

      if (selectedOption) {
        selectedOption.setAttribute('aria-selected', 'false');
      }

      const primaryOption = listNode.querySelector('li');

      if (primaryOption) {
        // Change the first option to be selected.
        primaryOption.setAttribute('aria-selected', 'true');

        // Change the toggle button to show the text of the primary option.
        toggleNode.querySelector('span').textContent = primaryOption.getAttribute('data-text');
      }
    });

    this.runFilter();
  }

  onPaginate() {
    this.page += 1;

    if (this.hasPaginateTarget) {
      this.paginateTarget.setAttribute('disabled', 'disabled');
    }

    this.runFilter(true).finally(() => {
      if (this.hasPaginateTarget) {
        this.paginateTarget.removeAttribute('disabled');
      }
    });
  }

  onToggleFilters() {
    const isOpen = this.filterTarget.getAttribute('aria-expanded') === 'true';

    this.visuallyToggleFilters(!isOpen);

    this.filterTarget.setAttribute('aria-expanded', !isOpen);

    if (!isOpen) {
      this.filterTarget.focus();
    } else {
      this.filterToggleTarget.focus();
    }
  }

  hasActiveFilters() {
    if (this.searchInputTarget.value) {
      return true;
    }

    return !!this.filters.find((filterNode) => {
      const filterInputNode = filterNode.querySelector('[data-target="select.inputField"]');

      return filterInputNode?.value;
    });
  }

  /**
   * @param {Boolean} appendResults
   * @returns {Promise<Object>}
   */
  runFilter(appendResults = false) {
    const formData = new FormData(this.filterFormTarget);

    formData.append('page', this.page);
    formData.append('search', this.searchInputTarget.value);

    this.updateFiltersCount(formData);

    SharedFilter.filtersToUrl(formData);

    if (this.hasResetFilterTarget) {
      this.resetFilterTarget.style.display = this.hasActiveFilters() ? 'block' : 'none';
    }

    if (this.hasBackToOverviewTarget) {
      this.backToOverviewTarget.style.display = 'none';
    }

    return this.callFilter(formData)
      .then((res) => {
        this.outputResults(res, appendResults);
        this.togglePaginateButton(res.next_page_url);

        if (!res.next_page_url) {
          if (this.hasBackToOverviewTarget) {
            this.backToOverviewTarget.style.display = this.page > 1 || this.hasActiveFilters() ? 'inline-block' : 'none';
          }
        }

        return res;
      })
      .then(this.outputResultsCount.bind(this))
      .catch(() => {
        if (this.hasPaginateTarget) {
          this.paginateTarget.removeAttribute('disabled');
        }
      });
  }

  /**
   * @param {Object} res
   * @param {boolean} appendResults
   * @returns {Object}
   */
  outputResults(res, appendResults) {
    if (!appendResults) {
      this.resultsTarget.innerHTML = '';
    }

    const newElementsWrapper = document.createElement('div');
    newElementsWrapper.innerHTML = res.data.join('');

    const newElements = toArray(newElementsWrapper.children);

    newElements.forEach((child) => {
      child.style.opacity = 0;
      this.resultsTarget.appendChild(child);
    });

    gsap.fromTo(newElements, {
      opacity: 0,
      y: 20,
    }, {
      opacity: 1,
      y: 0,
      stagger: 0.2,
      ease: 'power2.out',
    });

    return res;
  }

  /**
   * @param {Object} res
   * @returns {Object}
   */
  outputResultsCount(res) {
    this.resultsCountTargets.forEach((target) => {
      const resultTranslation = res.total === 1
        ? target.getAttribute('data-translation-result')
        : target.getAttribute('data-translation-results');

      if (resultTranslation.indexOf(':results') >= 0) {
        target.innerHTML = resultTranslation.replace(':results', res.total);
        return;
      }

      target.innerHTML = `<span>${res.total}</span> ${resultTranslation}`;
    });

    return res;
  }

  /**
   * @param {FormData} formData
   */
  updateFiltersCount(formData) {
    const formDataArray = formDataToArray(formData)
      .filter((item) => item[0] !== 'page' && item[0] !== 'search');

    const filtersCount = formDataArray.length;

    this.filterToggleCountTarget.querySelector('span').textContent = filtersCount;
    this.filterToggleCountTarget.style.opacity = filtersCount > 0 ? 1 : 0;
  }

  /**
   * @param {boolean} show
   */
  visuallyToggleFilters(show) {
    let toOpacity = 0;

    if (show) {
      // Start at an opacity of 0...
      this.filterTarget.style.opacity = 0;
      this.filterTarget.style.display = 'block';

      toOpacity = 1;
    }

    gsap.to(this.filterTarget, {
      opacity: toOpacity,
      ease: 'power2.out',
      duration: 0.5,
      onComplete: () => {
        if (!show) {
          this.filterTarget.style.display = 'none';
        }
      },
    });
  }

  /**
   * @param {FormData} formData
   */
  callFilter(formData) {
    return new Promise((resolve) => {
      fetch(this.data.get('api'), {
        method: 'POST',
        headers: {
          'X-CSRF-TOKEN': csrfToken(),
        },
        body: formData,
      })
        .then((res) => res.json())
        .then(resolve);
    });
  }

  togglePaginateButton(state) {
    if (this.hasPaginateTarget) {
      this.paginateTarget.style.display = state ? 'inline-block' : 'none';
    }
  }

  /**
   * @param {FormData} formData
   */
  static filtersToUrl(formData) {
    const params = formDataToArray(formData);

    const queryString = params
      .map((x) => `${encodeURIComponent(x[0])}=${encodeURIComponent(x[1])}`)
      .join('&');

    if (typeof window.history.replaceState === 'function') {
      window.history.replaceState(null, null, queryString ? `?${queryString}` : window.location.pathname);
    }
  }
}

SharedFilter.targets = [
  'filterBar',
  'filterBarBg',
  'filter',
  'filterToggle',
  'filterForm',
  'filterToggleCount',
  'resultsCount',
  'results',
  'paginate',
  'searchInput',
  'resetFilter',
  'backToOverview',
  'searchBar',
];

export default SharedFilter;
